import React, { useEffect, useState } from 'react';
import {
  FetchMoreType,
  usePaginationHelper,
  useQuery,
  useNavigate,
} from '@common/hooks';
import { startCase } from 'lodash';
import { Spacing } from 'works/legacy/lib/style-helper';
import {
  FilteredTable,
  buildOptionsFromData,
  MultiSelectTableFilter,
} from 'works/components/filtered-table';
import {
  DateRangeFilter,
  DateWithoutTimeZone,
  NumberRangeFilter,
  ErrorHandler,
  TextFilterSearch,
  Text,
  VisualAvailability,
  Currency,
  Link,
} from '@common/components';
import { StaffingRequestStateBadge } from 'works/components/staffing-request-state-badge';
import {
  PaginatedStaffingRequestsListQueryResponse,
  PaginatedStaffingRequestsListQueryVariables,
  SupplierStaffingRequestFiltersQuery,
} from '@applicant-flow/graphql/queries';
import { Moment } from 'moment';
import { RenderShiftType } from '@applicant-flow/components';
import { StaffingRequestType } from '@applicant-flow/types';

const ALL_STAFFING_REQUEST_TYPES = ['on_demand', 'long_term'];

type Props = {
  data: StaffingRequestType[];
  isLoaded: boolean;
  localStorageFilterKey: string;
  fetchMore: FetchMoreType<
    PaginatedStaffingRequestsListQueryResponse,
    PaginatedStaffingRequestsListQueryVariables
  >;
  total?: number;
};

export function RequestsTable({
  data,
  total = 0,
  isLoaded,
  fetchMore,
  localStorageFilterKey,
}: Props) {
  const navigate = useNavigate();
  const [paginationLoading, setPaginationLoading] = useState(false);

  const {
    filters,
    paginationVariables,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey,
    defaultPaginationData: {
      limit: 10,
      offset: 0,
      defaultPageSize: 10,
    },
    columnsDictionary: {
      code: 'ID',
      organizationBusiness: 'ORGANIZATION_BUSINESS',
      role: 'ROLE',
      shiftType: 'SHIFT_TYPE',
      shift: 'SHIFT',
      length: 'LENGTH',
      type: 'TYPE',
      rate: 'RATE',
      startDate: 'START',
      openCount: 'OPEN',
      submissionsCount: 'APPLICATIONS',
      acceptedCount: 'ACCEPTED',
      state: 'STATUS',
      clinicalUnit: 'CLINICAL_UNIT',
      location: 'LOCATION',
    },
  });

  useEffect(() => {
    setPaginationLoading(true);

    fetchMore({
      variables: {
        ...paginationVariables,
        staffingRequestTypes: ALL_STAFFING_REQUEST_TYPES,
      },
      updateQuery: (_prev, { fetchMoreResult }) => fetchMoreResult,
    }).then(() => setPaginationLoading(false));
  }, [paginationVariables, fetchMore]);

  const requestsDataWithStyle = data?.map((staffingRequest) => {
    if (['FILLED', 'CANCELLED'].includes(staffingRequest.state)) {
      return { ...staffingRequest, rowClassName: 'disabled-row' };
    }

    if (staffingRequest.state === 'ON_HOLD') {
      return { ...staffingRequest, rowClassName: 'on-hold-row' };
    }

    return staffingRequest;
  });

  const { error: filtersQueryError, data: filtersQueryData } = useQuery(
    SupplierStaffingRequestFiltersQuery,
    {
      skip: !isLoaded,
    }
  );

  const filtersDataReady = !!filtersQueryData;

  if (filtersQueryError) {
    return <ErrorHandler error={filtersQueryError} log="requests-table" />;
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'code',
      key: 'code',
      sorter: true,
      filtered: filters.code || false,
      filteredValue: filters.code || null,
      filterFormat: 'id',
      width: '100px',
      filterDropdown: ({
        setSelectedKeys,
        confirm,
      }: {
        setSelectedKeys: (values: string[]) => void;
        confirm: () => void;
      }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <TextFilterSearch
            onSearch={(value) => {
              value ? setSelectedKeys([value]) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: (code: string, request: StaffingRequestType) => (
        <Link to={buildStaffingRequestPath(request.id)}>
          <Text weight="bold" color="elm">
            {code}
          </Text>
        </Link>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'organizationBusiness',
      key: 'location',
      filtered: filters.location || false,
      filteredValue: filters.location || null,
      filters: buildOptionsFromData(
        filtersQueryData?.organizationBusinesses || [],
        (orgBusiness: { id: string; name: string }) => ({
          value: orgBusiness.id,
          text: orgBusiness.name,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      render: (_: string, request: StaffingRequestType) => {
        const organizationBusiness =
          request.organizationUnit.department.organizationBusiness;

        return (
          <>
            <div>
              <Text>{organizationBusiness.name}</Text>
            </div>
            <Text size="sm" color="midnightLighter">
              {`${organizationBusiness.city}, ${organizationBusiness.stateCode}`}
            </Text>
          </>
        );
      },
      sorter: true,
    },
    {
      title: 'Unit',
      dataIndex: ['organizationUnit', 'clinicalUnit', 'displayName'],
      key: 'clinicalUnit',
      filtered: filters.clinicalUnit || false,
      filteredValue: filters.clinicalUnit || null,
      filters: buildOptionsFromData(
        filtersQueryData?.worksClinicalUnits || [],
        (clinicalUnit: { id: string; displayName: string }) => ({
          value: clinicalUnit.id,
          text: clinicalUnit.displayName,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      sorter: true,
    },
    {
      title: 'Role',
      dataIndex: ['role', 'abbreviation'],
      key: 'role',
      width: '80px',
      sorter: true,
      filtered: filters.role || false,
      filteredValue: filters.role || null,
      filters: buildOptionsFromData(
        filtersQueryData?.roles || [],
        (role: { id: string; name: string }) => ({
          value: role.id,
          text: role.name,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Shift Type',
      dataIndex: 'shiftType',
      key: 'shiftType',
      filtered: filters.shiftType || false,
      filteredValue: filters.shiftType || null,
      filterDropdown: MultiSelectTableFilter,
      filters: [
        {
          text: <RenderShiftType shiftType="day" />,
          value: 'day',
        },
        {
          text: <RenderShiftType shiftType="evening" />,
          value: 'evening',
        },
        {
          text: <RenderShiftType shiftType="night" />,
          value: 'night',
        },
        {
          text: <RenderShiftType shiftType="variable" />,
          value: 'variable',
        },
      ],
      width: '100px',
      sorter: true,
      render: (shiftType: 'day' | 'evening' | 'night' | 'variable') => (
        <RenderShiftType shiftType={shiftType} />
      ),
    },
    {
      title: 'Shift',
      dataIndex: 'id',
      key: 'shift',
      width: '60px',
      sorter: true,
      render: (_: string, sr: StaffingRequestType) =>
        `${sr.shiftsPerWeek}x${sr.hoursPerShift}`,
    },
    {
      title: 'Length',
      dataIndex: 'length',
      key: 'length',
      width: '90px',
      sorter: true,
      filtered: filters.length || false,
      filteredValue: filters.length || null,
      filterDropdown: ({
        setSelectedKeys,
        confirm,
      }: {
        setSelectedKeys: (values: number[]) => void;
        confirm: () => void;
      }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <NumberRangeFilter
            onSearch={(values) => {
              values ? setSelectedKeys(values) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: (length: string) => `${length} weeks`,
    },
    {
      title: 'Type',
      dataIndex: 'staffingRequestType',
      key: 'type',
      filters: [
        {
          text: 'Long Term',
          value: 'long_term',
        },
        {
          text: 'On Demand',
          value: 'on_demand',
        },
      ],
      sorter: true,
      filtered: filters.staffingRequestType || false,
      filteredValue: filters.staffingRequestType || null,
      filterDropdown: MultiSelectTableFilter,
      width: '100px',
      render: (staffingRequestType: string) => startCase(staffingRequestType),
    },
    {
      title: 'Rate',
      dataIndex: 'billingRegularRate',
      key: 'rate',
      sorter: true,
      filterFormat: 'currencyRange',
      filtered: filters.rate || false,
      filteredValue: filters.rate || null,
      width: '125px',
      filterDropdown: ({
        setSelectedKeys,
        confirm,
      }: {
        setSelectedKeys: (values: number[]) => void;
        confirm: () => void;
      }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <NumberRangeFilter
            onSearch={(values) => {
              values ? setSelectedKeys(values) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: function renderRate(
        billingRegularRate: number,
        request: StaffingRequestType
      ) {
        const currentRate = request.billingRegularRate;
        const highestRate = request.highestRate;
        const lowestRate = request.lowestRate;

        if (highestRate !== lowestRate) {
          return (
            <>
              <Currency value={lowestRate} /> - <Currency value={highestRate} />
            </>
          );
        }

        return <Currency value={currentRate} />;
      },
    },
    {
      title: 'Start',
      dataIndex: 'startDate',
      key: 'startDate',
      sorter: true,
      filterFormat: 'dateRange',
      filtered: filters.startDate || false,
      filteredValue: filters.startDate || null,
      width: '90px',
      filterDropdown: ({
        setSelectedKeys,
        confirm,
      }: {
        setSelectedKeys: (values: (string | Moment | null)[]) => void;
        confirm: () => void;
      }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <DateRangeFilter
            autoFocus
            onSearch={(values) => {
              values ? setSelectedKeys(values) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: (startDate: string) => <DateWithoutTimeZone date={startDate} />,
    },
    {
      title: 'Open',
      dataIndex: 'id',
      key: 'openCount',
      sorter: true,
      width: '100px',
      render: (id: string, record: StaffingRequestType) => {
        const available = record.openStaffingRequestPositionsCount;
        const total = record.staffingRequestPositionsCount;

        return <VisualAvailability available={available} total={total} />;
      },
    },
    {
      title: 'Submissions',
      dataIndex: 'staffingRequestApplicationsCount',
      key: 'submissionsCount',
      sorter: true,
      width: '90px',
      render: (count: number) => (
        <div className="text-center">
          <Text color="skyRegular">{count}</Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      sorter: true,
      defaultSortOrder: 'ascend',
      filtered: filters.state || false,
      filteredValue: filters.state || null,
      filterDropdown: MultiSelectTableFilter,
      fixed: 'right',
      filters: [
        {
          text: 'Open',
          value: 'OPEN',
        },
        {
          text: 'Filled',
          value: 'FILLED',
        },
        {
          text: 'On Hold',
          value: 'ON_HOLD',
        },
        {
          text: 'Cancelled',
          value: 'CANCELLED',
        },
        {
          text: 'Pending',
          value: 'PENDING',
        },
      ],
      render: function renderState(
        state: string,
        request: StaffingRequestType
      ) {
        return (
          <StaffingRequestStateBadge
            state={state}
            timestamp={request.stateChangedAt}
            submissionsPaused={request.submissionsPaused}
          />
        );
      },
    },
  ];

  return (
    <>
      <FilteredTable
        rowKey="id"
        columns={columns}
        loading={!isLoaded || paginationLoading}
        dataSource={isLoaded ? requestsDataWithStyle : []}
        filtersDataReady={filtersDataReady}
        localStorageFilterKey={localStorageFilterKey}
        onRow={(record: StaffingRequestType) => ({
          onClick: () => navigate(buildStaffingRequestPath(record.id)),
        })}
        pagination={{
          ...paginationVariables,
          total,
        }}
        locale={{
          emptyText:
            'We’ll notify you of new Staffing Requests as they become available for your department.',
        }}
        onChange={tableChangeHandler}
        onFilterChange={filterChangeHandler}
        filters={undefined}
      />
    </>
  );
}

function buildStaffingRequestPath(id: string) {
  return `/works/supplier/staffing-request/${id}`;
}
